import React, {useState, useMemo, useEffect} from 'react';
import {useTable, useRowSelect} from 'react-table';
import {ContentWrapper, Button, SelectType, Switch, Input} from '@components';
import moment from 'moment';
import {
  getProducts,
  ImgUploadtApi,
  delProductApi,
  getProductDetail,
  UpdateProductApi,
  getProductReview,
  removeReview,
  getProductImages,
  deleteProductImage,
} from '@apis';
import {useHistory} from 'react-router-dom';
import toast from 'react-hot-toast';
import {useMutation} from 'react-query';
import axios from 'axios';
import {API_HOST} from '@apis/';
import './products.css';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';
import {ALLOWED_IMAGE_TYPES} from '../../utils/fileUtils';

const notificationTypes = [
  {label: 'off', value: 'off'},
  {label: 'on', value: 'on'},
];

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
  },
};
const IndeterminateCheckbox = React.forwardRef(
  ({indeterminate, ...rest}, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </>
    );
  },
);

const Table = ({columns, data}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: {selectedRowIds},
  } = useTable({columns, data}, useRowSelect, hooks => {
    hooks.visibleColumns.push(columns => [
      {
        id: 'selection',
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
      },
      ...columns,
    ]);
  });

  return (
    <table {...getTableProps()} className="min-w-full">
      <thead className="bg-tableHeaderBg p-4">
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th
                {...column.getHeaderProps()}
                className="text-left text-menu font-normal uppercase text-sm px-4 py-2">
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => {
                return (
                  <td
                    {...cell.getCellProps()}
                    className="p-4 whitespace-nowrap text-sm">
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Products = props => {
  const {t} = useTranslation();
  const history = useHistory();

  // State management
  const [name, setName] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [points_cost, setPointsCost] = useState(0);
  const [available_stock, setAvailableStock] = useState(0);
  const [description, setDescription] = useState('');
  const [descriptionEs, setDescriptionEs] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productImages, setProductImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [isEditedView, setIsEditedView] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [originalImageFiles, setOriginalImageFiles] = useState([]);
  const [productLoading, setProductLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  // Form validation
  const isFormValid = useMemo(() => {
    const isNameValid = name.trim().length > 0 && name.trim().length <= 100;
    const isNameEsValid =
      nameEs.trim().length > 0 && nameEs.trim().length <= 100;
    const isDescriptionValid =
      description.trim().length > 0 && description.trim().length <= 500;
    const isDescriptionEsValid =
      descriptionEs.trim().length > 0 && descriptionEs.trim().length <= 500;
    const isCostValid = points_cost > 0;
    const isStockValid = available_stock > 0;

    return (
      isNameValid &&
      isNameEsValid &&
      isDescriptionValid &&
      isDescriptionEsValid &&
      isCostValid &&
      isStockValid
    );
  }, [name, nameEs, description, descriptionEs, points_cost, available_stock]);

  // Mutations
  const updateProductMutation = useMutation(
    async productData => {
      return await UpdateProductApi(productData);
    },
    {
      onSuccess: () => {
        setIsEditedView(false);
        getProductInfo();
      },
      onError: error => {
        console.error('Error updating product:', error);
        toast.error(
          t('Productdetails.updateError') || 'Error updating product',
        );
      },
    },
  );

  const uploadImageMutation = useMutation(
    async ({productId, originalFile}) => {
      if (!originalFile) return;

      const formData = new FormData();
      formData.append('image', originalFile);
      return await ImgUploadtApi({id: productId, formdata: formData});
    },
    {
      onSuccess: () => {
        toast.success(
          t('Productdetails.imageUploadSuccess') ||
            'Image uploaded successfully',
        );
        fetchProductImages(); // Refresh images
      },
      onError: error => {
        console.error('Error uploading image:', error);
        toast.error(
          t('Productdetails.imageUploadError') || 'Error uploading image',
        );
      },
    },
  );

  const deleteProductMutation = useMutation(
    async productId => {
      return await delProductApi(productId);
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          history.replace('/products');
        }, 1000);
      },
      onError: error => {
        console.error('Error deleting product:', error);
        toast.error(
          t('Productdetails.deleteError') || 'Error deleting product',
        );
      },
    },
  );

  const deleteImageMutation = useMutation(
    async ({productId, imageId}) => {
      return await deleteProductImage(productId, imageId);
    },
    {
      onSuccess: () => {
        fetchProductImages(); // Refresh images after deletion
      },
      onError: error => {
        console.error('Error deleting image:', error);
        toast.error(
          t('Productdetails.imageDeleteError') || 'Error deleting image',
        );
      },
    },
  );

  // Handle image deletion
  const handleDeleteImage = async (imageId) => {
    console.log('Attempting to delete image with ID:', imageId);
    console.log('Image ID type:', typeof imageId);
    
    if (!imageId) {
      console.error('Image ID is undefined or null');
      toast.error(t('Productdetails.imageDeleteError') || 'Error: Image ID is missing');
      return;
    }
    
    // Set the image to delete and show modal
    setImageToDelete(imageId);
    setShowDeleteModal(true);
  };

  // Confirm image deletion
  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;
    
    try {
      await deleteImageMutation.mutateAsync({
        productId: props.params,
        imageId: imageToDelete,
      });
      toast.success(t('Productdetails.imageDeleted') || 'Image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error(t('Productdetails.imageDeleteError') || 'Error deleting image');
    } finally {
      setShowDeleteModal(false);
      setImageToDelete(null);
    }
  };

  // Cancel image deletion
  const cancelDeleteImage = () => {
    setShowDeleteModal(false);
    setImageToDelete(null);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showDeleteModal) {
        cancelDeleteImage();
      }
    };

    if (showDeleteModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
      
      // Add emergency function to window for debugging
      window.emergencyCloseModal = () => {
        setShowDeleteModal(false);
        setImageToDelete(null);
        document.body.style.overflow = 'unset';
        console.log('Modal closed via emergency function');
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset'; // Restore scroll
      delete window.emergencyCloseModal;
    };
  }, [showDeleteModal]);

  // Fetch product reviews
  const fetchData = async () => {
    try {
      const review = await getProductReview(props.params);
      if (review?.results && review.results.length > 0) {
        let data = review.results.map(e => {
          e.created_at = moment(e.created_at).format('YYYY-MM-DD');
          return e;
        });
        setData(review.results);
      } else {
        // No reviews found, set empty array
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // If reviews endpoint doesn't exist or fails, just show no reviews
      setData([]);
      // Don't show error toast for reviews since it's not critical
    } finally {
      setLoading(false);
    }
  };

  // Fetch product information
  const getProductInfo = async () => {
    try {
      setProductLoading(true);
      const response = await getProductDetail(props.params);

      // Check if response is HTML (indicates routing/auth issue)
      if (
        typeof response === 'string' &&
        response.includes('<!doctype html>')
      ) {
        console.error(
          'API returned HTML instead of JSON - likely authentication or routing issue',
        );
        toast.error('Authentication error. Please try logging in again.');
        return;
      }

      // Handle multilingual fields with pre-population
      const productNameEn = response.name_en || response.name || '';
      const productNameEs =
        response.name_es_mx || productNameEn; // Pre-populate with English if Spanish is empty
      const productDescriptionEn =
        response.description_en || response.description || '';
      const productDescriptionEs =
        response.description_es_mx || productDescriptionEn; // Pre-populate with English if Spanish is empty

      setName(productNameEn);
      setNameEs(productNameEs);
      setPointsCost(response.points_cost || 0);
      setDescription(productDescriptionEn);
      setDescriptionEs(productDescriptionEs);
      setAvailableStock(response.available_stock || 0);
      setIsGift(response.is_gift || false);
      setEnabled(response.enabled || false);
    } catch (error) {
      console.error('Error fetching product info:', error);
      toast.error(
        t('Productdetails.loadError') || 'Error loading product information',
      );
    } finally {
      setProductLoading(false);
    }
  };

  // Fetch product images
  const fetchProductImages = async () => {
    try {
      setImageLoading(true);
      const response = await getProductImages(props.params);
      
      setProductImages(response);
    } catch (error) {
      console.error('Error fetching product images:', error);
    } finally {
      setImageLoading(false);
    }
  };

  // Handle image file selection
  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/svg+xml',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          t('common.invalidFileType') ||
            'Only JPG, PNG and SVG images are allowed',
        );
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(
          t('common.fileSizeError') || 'File size should be less than 5 MB',
        );
        return;
      }

      // Generate unique ID for this image
      const imageId = Date.now() + Math.random();

      // Store original file for upload
      setOriginalImageFiles(prev => [...prev, { file, id: imageId }]);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = e => {
        setNewImages(prev => [...prev, {
          src: e.target.result,
          id: imageId,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    }

    // Clear the input value so the same file can be selected again if needed
    e.target.value = '';
  };

  // Handle removing individual preview images
  const handleRemovePreview = (imageId) => {
    setNewImages(prev => prev.filter(img => img.id !== imageId));
    setOriginalImageFiles(prev => prev.filter(img => img.id !== imageId));
  };

  // Handle product update
  const handleUpdateProduct = async () => {
    if (!isFormValid) {
      toast.error(
        t('Productdetails.validationError') ||
          'Please fill in all required fields with valid values',
      );
      return;
    }

    setIsSaving(true);

    try {
      // Update product information with multilingual fields
      await updateProductMutation.mutateAsync({
        id: props.params,
        name_en: name.trim(),
        name_es_mx: nameEs.trim(),
        points_cost: Number(points_cost),
        available_stock: Number(available_stock),
        description_en: description.trim(),
        description_es_mx: descriptionEs.trim(),
        enabled: enabled,
        is_gift: isGift,
      });

      // Upload new images if provided
      if (originalImageFiles.length > 0) {
        // Upload images sequentially
        for (const imageFile of originalImageFiles) {
          await uploadImageMutation.mutateAsync({
            productId: props.params,
            originalFile: imageFile.file,
          });
        }
        // Clear preview after all uploads complete
        setNewImages([]);
        setOriginalImageFiles([]);
      }
    } catch (error) {
      console.error('Error updating product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async () => {
    if (
      window.confirm(
        t('Productdetails.deleteConfirm') ||
          'Are you sure you want to delete this product?',
      )
    ) {
      await deleteProductMutation.mutateAsync(props.params);
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    setIsEditedView(false);
  };

  // Handle review removal
  const handleRemoveReview = async reviewId => {
    try {
      await removeReview(reviewId);
      toast.success(
        t('Productdetails.reviewDeleted') || 'Review deleted successfully',
      );
      fetchData(); // Refresh reviews
    } catch (error) {
      console.error('Error removing review:', error);
      toast.error(
        t('Productdetails.reviewDeleteError') || 'Error deleting review',
      );
    }
  };

  // Effects
  useEffect(() => {
    fetchProductImages();
    getProductInfo();
    fetchData();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: t('Productdetails.user'),
        accessor: 'author_name',
        Cell: ({value}) => (
          <div className="flex items-center">
            <div className="px-4">
              <p className="text-left">{value}</p>
            </div>
          </div>
        ),
      },
      {
        Header: t('Productdetails.score'),
        accessor: 'rating',
      },
      {Header: t('Productdetails.date'), accessor: 'created_at'},
      {Header: t('Productdetails.comment'), accessor: 'comment'},
      {
        Header: t('Productdetails.action'),
        accessor: 'id',
        Cell: ({row}) => (
          <div>
            <Button
              label={t('Productdetails.remove')}
              onClick={() => handleRemoveReview(row.original.id)}
              type="button"
            />
          </div>
        ),
      },
    ],
    [t],
  );
  return (
    <ContentWrapper
      breadcrumb={[
        {title: t('sidebar.menuItems.inventory'), link: '/products'},
        {title: name},
      ]}
      productDetail={true}
      hasSearch={false}
      removeShopUser={false}
      onButtonThreeClick={handleDeleteProduct}
      onButtonOneClick={handleUpdateProduct}
      onButtonTwoClick={handleCancel}
      isEditedView={isEditedView}>
      <div className="flex flex-col w-full">
        <div className="product-images-row">
          {!imageLoading &&
            productImages?.map(item => (
              <div key={item.id} className="product-image-container">
                <img
                  className="object-contain"
                  src={item.image}
                  alt="Store Logo"
                />
                {/* Delete button overlay */}
                <div className="delete-overlay">
                  <button
                    onClick={() => handleDeleteImage(item.id)}
                    className="delete-button"
                    title={t('Productdetails.deleteImage') || 'Delete Image'}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          {/* Preview for newly selected images */}
          {newImages.map(image => (
            <div key={image.id} className="product-image-container">
              <img
                className="object-contain"
                src={image.src}
                alt={`Preview: ${image.name}`}
              />
              {/* Remove preview button overlay */}
              <div className="delete-overlay">
                <button
                  onClick={() => handleRemovePreview(image.id)}
                  className="delete-button"
                  title={t('Productdetails.removePreview') || 'Remove Preview'}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
          <div>
            <label htmlFor="image-upload">
              <div className="upload-button">
                <h4>{t('Productdetails.addImage')}</h4>
              </div>
            </label>
          </div>
        </div>
      </div>
      <Input
        type="file"
        accept={ALLOWED_IMAGE_TYPES.join(',')}
        onChange={handleImageChange}
        id="image-upload"
        hidden
      />

      {productLoading ? (
        <div className="text-center py-8">
          <p>{t('common.loading')} product information...</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.nameEnglish')}
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('Productdetails.nameEnglish')} (${t(
                'Productdetails.maxCharacters',
                {count: 100},
              )})`}
              disabled={isSaving}
              maxLength={100}
            />
            {name.length > 90 && (
              <p className="text-sm text-gray-600 mt-1">
                {t('Productdetails.charactersCount', {
                  current: name.length,
                  max: 100,
                })}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.nameSpanish')}
            </label>
            <input
              type="text"
              value={nameEs}
              onChange={e => setNameEs(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('Productdetails.nameSpanish')} (${t(
                'Productdetails.maxCharacters',
                {count: 100},
              )})`}
              disabled={isSaving}
              maxLength={100}
            />
            {nameEs.length > 90 && (
              <p className="text-sm text-gray-600 mt-1">
                {t('Productdetails.charactersCount', {
                  current: nameEs.length,
                  max: 100,
                })}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.pointCost')}
            </label>
            <input
              type="number"
              value={points_cost}
              onChange={e => setPointsCost(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('Productdetails.pointCost')}
              min="1"
              disabled={isSaving}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.availableStock')}
            </label>
            <input
              type="number"
              value={available_stock}
              onChange={e => setAvailableStock(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('Productdetails.availableStock')}
              min="1"
              disabled={isSaving}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.descriptionEnglish')}
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('Productdetails.descriptionEnglish')} (${t(
                'Productdetails.maxCharacters',
                {count: 500},
              )})`}
              rows="4"
              disabled={isSaving}
              maxLength={500}
            />
            {description.length > 450 && (
              <p className="text-sm text-gray-600 mt-1">
                {t('Productdetails.charactersCount', {
                  current: description.length,
                  max: 500,
                })}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              {t('Productdetails.descriptionSpanish')}
            </label>
            <textarea
              value={descriptionEs}
              onChange={e => setDescriptionEs(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('Productdetails.descriptionSpanish')} (${t(
                'Productdetails.maxCharacters',
                {count: 500},
              )})`}
              rows="4"
              disabled={isSaving}
              maxLength={500}
            />
            {descriptionEs.length > 450 && (
              <p className="text-sm text-gray-600 mt-1">
                {t('Productdetails.charactersCount', {
                  current: descriptionEs.length,
                  max: 500,
                })}
              </p>
            )}
          </div>

          <div className="checkbox-row">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="enabled"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
                disabled={isSaving}
              />
              <label htmlFor="enabled" className="ml-2">
                {t('Productdetails.enabled')}
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isGift"
                checked={isGift}
                onChange={e => setIsGift(e.target.checked)}
                disabled={isSaving}
              />
              <label htmlFor="isGift" className="ml-2">
                {t('Productdetails.isGift')}
              </label>
            </div>
          </div>

          <div className="button-row">
            <Button
              label={isSaving ? t('common.loading') : t('Productdetails.save')}
              onClick={handleUpdateProduct}
              disabled={!isFormValid || isSaving}
              type="button"
            />
            <Button
              label={t('Productdetails.remove')}
              onClick={handleDeleteProduct}
              disabled={isSaving}
              type="button"
              className="bg-red-500 hover:bg-red-600"
            />
          </div>
        </>
      )}

      <div className="my-5">
        <h5 className="mb-4">{t('Productdetails.productReviews')}</h5>
        {loading ? (
          <div className="text-center py-4">
            <p>{t('common.loading')}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-4">
            <p>{t('Productdetails.noReviews')}</p>
          </div>
        ) : (
          <Table columns={columns} data={data} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={cancelDeleteImage}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              margin: '0 16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ flexShrink: 0 }}>
                <svg
                  style={{ width: '24px', height: '24px', color: '#dc2626' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div style={{ marginLeft: '12px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#111827' }}>
                  {t('Productdetails.deleteImage')}
                </h3>
              </div>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                {t('Productdetails.deleteImageConfirm')}
              </p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={cancelDeleteImage}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDeleteImage}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'white',
                  backgroundColor: '#dc2626',
                  border: '1px solid transparent',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ContentWrapper>
  );
};

export default Products;
